import type { CheckResult, StepRole, TaskStatus } from './types.ts';

export interface StepState {
  status: TaskStatus;
  step: number;
}

/**
 * Wendet die Schritt-Rolle einer neuen Nachricht auf den Aufgabenstatus an.
 *
 *   offen ─► in_arbeit ─► zur_pruefung ─► erledigt
 *               ▲               │
 *               └── nicht_ok ───┘   (neuer Schritt)
 *
 * Gibt den neuen Zustand und den Schritt zurück, dem die Nachricht zugeordnet
 * wird. Wird identisch auf Server (maßgeblich) und Client (optimistisch)
 * ausgeführt.
 */
export function applyStepRole(
  current: StepState,
  role: StepRole | null,
  check: CheckResult | null,
): StepState & { messageStep: number } {
  const { status, step } = current;

  if (!role) return { status, step, messageStep: step };

  switch (role) {
    case 'problem': {
      // Ein neues Problem nach einer Lösung oder in einer erledigten Aufgabe
      // eröffnet den nächsten Schritt. Mehrere Problem-Nachrichten
      // hintereinander bleiben im selben Schritt.
      const opensNewStep = status === 'zur_pruefung' || status === 'erledigt';
      const nextStep = opensNewStep ? step + 1 : Math.max(step, 1);
      return { status: 'offen', step: nextStep, messageStep: nextStep };
    }
    case 'loesung':
      return { status: 'zur_pruefung', step, messageStep: step };
    case 'pruefung': {
      if (check === 'ok') return { status: 'erledigt', step, messageStep: step };
      // Nicht ok: Die Prüfung gehört bereits zum neuen Schritt, weil sie das
      // nächste Problem beschreibt.
      const nextStep = step + 1;
      return { status: 'in_arbeit', step: nextStep, messageStep: nextStep };
    }
  }
}

export const STATUS_LABEL: Record<TaskStatus, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  zur_pruefung: 'Zur Prüfung',
  erledigt: 'Erledigt',
};

/** Status für die Oberfläche: dort gibt es nur offen und erledigt. */
export function statusText(status: TaskStatus): string {
  return status === 'erledigt' ? 'Erledigt' : 'Offen';
}

export const STEP_ROLE_LABEL: Record<StepRole, string> = {
  problem: 'Problem',
  loesung: 'Lösung',
  pruefung: 'Prüfung',
};
